import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tvShows, categories, categorySlugMap } from '@/mocks/tvShows';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import TVShowCard from '@/components/base/TVShowCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = slug ? categorySlugMap[slug] || '国产剧' : '国产剧';
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'episodes'>('rating');

  const filteredShows = useMemo(() => {
    let shows = tvShows.filter((s) => s.category === categoryName);
    if (sortBy === 'rating') {
      shows = [...shows].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      shows = [...shows].sort((a, b) => b.year - a.year);
    } else {
      shows = [...shows].sort((a, b) => b.episodes - a.episodes);
    }
    return shows;
  }, [categoryName, sortBy]);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${categoryName}在哪里可以免费观看？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `在${categoryName}页面，您可以免费观看被窝网电影提供的所有${categoryName}资源，无需充值，全集免费高清在线观看。`,
        },
      },
      {
        '@type': 'Question',
        name: `${categoryName}有什么热门推荐？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `本页面展示了被窝网电影上热门的${categoryName}，按评分从高到低排列，您可以根据自己的喜好选择观看。`,
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <main>
        <Navbar />
        <div className="pt-20 pb-4 bg-background-100">
          <div className="max-w-7xl mx-auto w-full">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 bg-background-100">
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {categoryName}
                </h1>
                <span className="text-foreground-500 text-sm">
                  共 {filteredShows.length} 部
                </span>
              </div>

              <div className="flex items-center gap-2 bg-background-50 rounded-lg p-1">
                <button
                  onClick={() => setSortBy('rating')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === 'rating'
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  按评分
                </button>
                <button
                  onClick={() => setSortBy('year')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === 'year'
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  按年份
                </button>
                <button
                  onClick={() => setSortBy('episodes')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === 'episodes'
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  按集数
                </button>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-nowrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    cat.name === categoryName
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredShows.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredShows.map((show) => (
                  <TVShowCard key={show.id} show={show} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-50 rounded-full">
                  <i className="ri-movie-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的电视剧资源</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}